import React, { createContext, useContext, useEffect, useState, useCallback, useRef, } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const CategoryContext = createContext();
export const useCategories = () => useContext(CategoryContext);

export const CategoryProvider = ({ children }) => {
    const { token, adminId, isReady, logout } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);

    // If your BASE_URL already includes `/api`, keep this as `/categories`
    // If BASE_URL = "http://localhost/printing/public", change to "/api/categories"
    const CATEGORIES_ENDPOINT = "/categories";
    const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
    const imageURL = CDN_URL.endsWith("/") ? CDN_URL.slice(0, -1) : CDN_URL;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
        mountedRef.current = false;
        };
    }, []);

    const safeSetCategories = (updater) => {
        if (!mountedRef.current) return;
        setCategories(updater);
    };

    const fetchCategories = useCallback(async () => {
        setLoading(true);
        try {
        if (!token) throw new Error("Missing auth token");

        const url = `${baseURL}${CATEGORIES_ENDPOINT}`;
        console.debug("[Categories] GET", url);

        const res = await fetch(url, {
            method: "GET",
            headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            },
        });

        const payload = await res.json().catch(() => null);

        if (!res.ok) {
            const message = payload?.message || `Fetch failed (${res.status})`;
            throw new Error(message);
        }

        // normalize response shapes
        const items = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.categories)
            ? payload.categories
            : Array.isArray(payload)
            ? payload
            : payload?.items ?? [];
        
        const processed = items.map(c => ({
            ...c,
            image: c.image ? `${imageURL}/${c.image}` : "/no-image.png",
            status: c.status,
        }));

        safeSetCategories(processed || []);

        return payload;
        } catch (err) {
        console.error("[Categories] fetchCategories error:", err);
        if (err.message?.includes("Token Signature could not be verified")) {
            console.warn("[Auth] Invalid token — logging out automatically");
            logout();        // <-- call logout
            return;
        }
        safeSetCategories([]);
        throw err;
        } finally {
        if (mountedRef.current) setLoading(false);
        }
    }, [token, baseURL]);

    const addCategory = useCallback(
        async (categoryData) => {
            try {
            if (!token) throw new Error("Missing auth token");

            const url = `${baseURL}${CATEGORIES_ENDPOINT}`;
            console.debug("[Categories] POST", url);

            // categoryData is already FormData
            const res = await fetch(url, {
                method: "POST",
                headers: {
                Authorization: `Bearer ${token}`, // Do NOT set Content-Type for FormData
                },
                body: categoryData,
            });

            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(payload?.message || `Add failed (${res.status})`);
            }

            await fetchCategories();
            return payload;
            } catch (err) {
            console.error("[Categories] addCategory error:", err);
            throw err;
            }
        },
        [token, baseURL, fetchCategories]
    );

    const updateCategory = useCallback(
        async (id, categoryData) => {
            try {
            if (!token) throw new Error("Missing auth token");

            const url = `${baseURL}${CATEGORIES_ENDPOINT}/${id}`;
            console.debug("[Categories] UPDATE", url);

            const res = await fetch(url, {
                method: "POST", // or PATCH depending on your API
                headers: {
                Authorization: `Bearer ${token}`, // NO Content-Type
                },
                body: categoryData, // <-- FormData from modal
            });

            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(payload?.message || `Update failed (${res.status})`);
            }

            await fetchCategories(); // refresh categories
            return payload;
            } catch (err) {
            console.error("[Categories] updateCategory error:", err);
            throw err;
            }
        },
        [token, baseURL, fetchCategories]
        );


    const deleteCategory = useCallback(
        async (id) => {
        try {
            if (!token) throw new Error("Missing auth token");

            const auditId = localStorage.getItem("admin_id") || adminId || null;
            const url = `${baseURL}${CATEGORIES_ENDPOINT}/${id}`;
            console.debug("[Categories] DELETE", url);

            const res = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
                Accept: "application/json",
            },
            body: JSON.stringify({ delete_audit_id: auditId }),
            });

            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
            throw new Error(payload?.message || `Delete failed (${res.status})`);
            }

            // remove locally for snappy UI
            safeSetCategories((prev) => prev.filter((c) => String(c.id) !== String(id)));

            return payload;
        } catch (err) {
            console.error("[Categories] deleteCategory error:", err);
            throw err;
        }
        },
        [token, baseURL, adminId]
    );

    const toggleStatus = useCallback(
  async (id, currentStatus) => {
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    try {
      if (!token) throw new Error("Missing auth token");

      const auditId = localStorage.getItem("admin_id") || adminId || null;

      // Optimistic UI update
      safeSetCategories((prev) =>
        prev.map((c) =>
          String(c.id) === String(id) ? { ...c, status: newStatus } : c
        )
      );

      const url = `${baseURL}${CATEGORIES_ENDPOINT}/${id}/status`;
      console.debug("[Categories] PATCH (toggle status)", url);

      const res = await fetch(url, {
        method: "PATCH", // use PATCH for status update
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ status: newStatus, update_audit_id: auditId }),
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload?.message || `Toggle failed (${res.status})`);
      }

      return payload;
    } catch (err) {
      console.error("[Categories] toggleStatus error:", err);
      // rollback on failure
      safeSetCategories((prev) =>
        prev.map((c) =>
          String(c.id) === String(id) ? { ...c, status: currentStatus } : c
        )
      );
      throw err;
    }
  },
  [token, baseURL, adminId]
);



    useEffect(() => {
        if (!isReady) return;
        if (!token) {
            console.warn("[Categories] token not available — skipping fetch");
            return;
        }

        // initial load
        fetchCategories().catch((err) =>
        console.warn("[Categories] initial fetch failed:", err.message)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isReady, token]);

    return (
        <CategoryContext.Provider
        value={{
            categories,
            loading,
            fetchCategories,
            addCategory,
            updateCategory,
            deleteCategory,
            toggleStatus,
        }}
        >
        {children}
        </CategoryContext.Provider>
    );
};
